import { GripVertical, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Constraint {
  id?: string
  content: string
  order: number
}

interface ConstraintsSectionProps {
  constraints: Constraint[]
  onUpdate: (constraints: Constraint[]) => void
}

export function ConstraintsSection({
  constraints,
  onUpdate,
}: ConstraintsSectionProps) {
  const handleAddConstraint = () => {
    const newConstraint: Constraint = {
      content: '',
      order: constraints.length,
    }
    onUpdate([...constraints, newConstraint])
  }

  const handleUpdateConstraint = (index: number, content: string) => {
    const updated = [...constraints]
    updated[index] = { ...updated[index], content }
    onUpdate(updated)
  }

  const handleRemoveConstraint = (index: number) => {
    const updated = constraints.filter((_, i) => i !== index)
    onUpdate(updated.map((c, i) => ({ ...c, order: i })))
  }

  const handleMoveConstraint = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === constraints.length - 1)
    ) {
      return
    }

    const updated = [...constraints]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    ;[updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]]
    onUpdate(updated.map((c, i) => ({ ...c, order: i })))
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button type="button" onClick={handleAddConstraint} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Constraint
        </Button>
      </div>

      {constraints.length === 0 ? (
        <div className="border-border bg-muted/50 flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <p className="text-muted-foreground mb-4">
            No constraints added yet.
          </p>
          <Button type="button" onClick={handleAddConstraint} variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            Add First Constraint
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {constraints.map((constraint, index) => (
            <div
              key={constraint.id || `constraint-${index}`}
              className="border-border bg-muted/30 rounded-lg border p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="text-muted-foreground h-4 w-4" />
                  <span className="text-sm font-medium">
                    Constraint {index + 1}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveConstraint(index, 'up')}
                    disabled={index === 0}
                    className="h-7 w-7 p-0"
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMoveConstraint(index, 'down')}
                    disabled={index === constraints.length - 1}
                    className="h-7 w-7 p-0"
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveConstraint(index)}
                    className="text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <textarea
                value={constraint.content}
                onChange={(e) => handleUpdateConstraint(index, e.target.value)}
                placeholder="1 <= n <= 10^4"
                className="border-input bg-background min-h-[80px] w-full resize-y rounded-md border px-3 py-2 text-sm"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
